import * as Sentry from "@sentry/react";
import { v4 as uuidv4 } from "uuid";
import {
  MessagingSession,
  MessagingSessionObserver,
} from "amazon-chime-sdk-js";
import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useState,
} from "react";
import {
  Channel,
  ChannelMembershipSummary,
  ChannelMessage as AWSChannelMessage,
  ChannelMessageSummary,
  ChimeSDKMessagingClient,
  ListChannelMessagesCommand,
  MessageAttributeValue,
  SendChannelMessageCommand,
} from "@aws-sdk/client-chime-sdk-messaging";

import chatService from "../chatService";
import { parseISO } from "date-fns";
import { To, useParams, useSearchParams } from "react-router-dom";
import useProviderDetails from "../../../../provider-portal/useProviderDetails";
import usePatients, { usePatientDetails } from "../../hooks/usePatients";
import useAuth from "../../hooks/useAuth";
import usePatientProviders from "../../hooks/usePatientProviders";
import { useMutation } from "@tanstack/react-query";
import toast from "react-hot-toast";
import { logger } from "../../../logger";
import { generateFileKey } from "../../../../provider-portal/useFiles";
import useLoadingQueue from "./hooks/useLoadingQueue";

export interface ChatUser {
  chimeArn: string;
  cognitoId: string;
  patientOrProviderId: string;
  isProvider: boolean;
  hasActiveRelationship: boolean;
  firstName: string;
  lastName: string;

  displayName: string;
  avatarUrl: string;
  createdAt: Date | null;
  profileLink?: To | undefined;
}

interface SocketChannelMessage
  extends Omit<
    Omit<AWSChannelMessage, "CreatedTimestamp">,
    "LastUpdatedTimestamp"
  > {
  ChannelArn: string;
  CreatedTimestamp: string;
  LastUpdatedTimestamp: string;
}

interface SocketChannel extends Omit<Channel, "LastMessageTimestamp"> {
  LastMessageTimestamp: string;
}

interface SocketChannelDetails {
  ChannelMemberships: ChannelMembershipSummary[];
  ChannelMessages: SocketChannelMessage[];
  Channel: SocketChannel;
  ChannelMessagesHasMore: boolean;
}

type BaseMessage = {
  messageId: string;
  lastUpdatedTimestamp: Date;
  createdTimestamp: Date;
  senderArn: string;
  sender: ChatUser;
};

export type StandardMessage = BaseMessage & {
  type: "STANDARD";
  content: string;
  attributes: FayMessageAttributes;
};

export type ControlMessage = BaseMessage & {
  type: "CONTROL";
  content: "READ" | "UNKNOWN";
};

// We pass some messages without sender around the reducer and so having this type defined concretely
// is useful for proper interfacing. When we process the message inside the reducer, we bind the sender
// and these therefore become full ChannelMessages.
// The repetitive Omits are because TypeScript doesn't seem to like Omit on an |'d type.
type ChannelMessageWithoutSender =
  | Omit<StandardMessage, "sender">
  | Omit<ControlMessage, "sender">;
export type ChannelMessage = StandardMessage | ControlMessage;

export type ConversationSortKey = "lastMessageDate" | "createdAt";

export type Conversation = {
  id: string;
  name: string;
  selfChimeArn: string;
  self: ChatUser;
  respondentChimeArn: string;
  respondent: ChatUser;
  hasUnreadMessage: boolean;
  nextConversationMessagesToken?: {
    nextToken: string;
    notAfter: Date | null;
  };
  lastMessageDate: Date | null;
  messages: StandardMessage[];
  controlMessages: ControlMessage[];
  hasTriedToLoadOrReceivedMessages: boolean;
  shouldAttemptLoadingChannel: boolean;
  hasMoreMessages: boolean;
  sortKey: ConversationSortKey;
};

type ChatState = {
  isConnecting: boolean;
  session?: MessagingSession;
  messagingClient?: ChimeSDKMessagingClient;
  conversations: Conversation[];
  unreadConversationCount: number;
  clientSearchQuery: string;
  setClientSearchQuery: (query: string) => void;
  selectedConversation: Conversation | null;
  sendChannelMessage: ({
    message,
    attributes,
    recipientId,
  }: {
    message: string;
    attributes?: FayMessageAttributes;
    recipientId?: string;
  }) => Promise<void>;
  isSendingChannelMessage: boolean;
  // Assumes the current selected conversation
  sendChannelReadReceipt: (conversation?: Conversation) => Promise<void>;
  loadMoreChatHistoryForConversation: (conversation: string) => Promise<void>;
  loadMoreChatHistoryForSelectedConversation: () => void;
  shouldAutomaticallyMarkSelectedConversationAsRead?: boolean;
  respondentEntityName: string;
  chatBoxPlaceholderText: string;
  respondentDisplayNameModifier?: string;
  respondentProfileUrl?: To;
  shouldSuggestDrafts: boolean;
  enqueueLoadConversation: (conversationId: string) => void;
};

const ChatContext = createContext<ChatState | undefined>(undefined);

export interface FayMessageAttributes {
  attachments?: { fileName: string; fileObjectKey: string }[];
  format: "plain" | "html";
  // This allows us to override the sent time for imported messages
  sentTimeOverride?: Date;
  journalEntryId?: string;
  patientLabId?: string;
}

export interface MessageAttributeMapper {
  toChimeMessageAttributes: (
    attributes: FayMessageAttributes,
  ) => Record<string, MessageAttributeValue> | undefined;
  parseChimeMessageAttributes: (
    attributes: Record<string, MessageAttributeValue>,
  ) => FayMessageAttributes;
}

// Chime message attributes have a character limit of 512.
// There is 157 characters of boilerplate, and the
// file name is listed twice in the message attributes.
// 154 (rounding down to 150) is a safe limit for the file name.
export const MAX_FILENAME_LENGTH = 100;
export const MAX_EXTENSION_LENGTH = 25;
export const CHIME_MAX_ATTRIBUTE_SIZE = 2048;

export const estimateAttachmentAttributeSize = (
  fileNames: string[],
): number => {
  const attributes = messageAttributeMapper.toChimeMessageAttributes({
    attachments: fileNames.map((fileName) => ({
      fileName,
      fileObjectKey: generateFileKey({
        providerId: String(uuidv4()),
        patientId: String(uuidv4()),
        fileName,
      }),
    })),
    format: "plain",
  });

  return JSON.stringify(attributes).length;
};

/**
 * Returns true if the conversation has an unread message
 * given the last read control message and the last message from respondent.
 */
function checkHasUnreadMessages(
  readerArn: string,
  controlMessages: ControlMessage[],
  standardMessages: StandardMessage[],
): boolean {
  // Get READ state from control messages
  // Get last read control message
  const lastReadControlMessage = controlMessages.findLast(
    (message) => message.content === "READ" && message.senderArn === readerArn,
  );
  // Get last message from respondent
  const lastMessageFromRespondent = standardMessages.findLast(
    (message) => message.senderArn !== readerArn,
  );

  const hasUnreadMessage =
    lastMessageFromRespondent &&
    (!lastReadControlMessage ||
      lastReadControlMessage.createdTimestamp <
        lastMessageFromRespondent.createdTimestamp);
  return hasUnreadMessage === undefined ? false : hasUnreadMessage;
}

/**
 * Takes an array of messages and merges them with the existing conversation's
 * control and standard messages.
 * Returns the new control and standard messages.
 */
function mergeControlAndStandardMessages(
  existingStandardMessages: StandardMessage[],
  existingControlMessages: ControlMessage[],
  messages: ChannelMessage[],
) {
  // TODO: allow richer text formatting
  const standardMessages = existingStandardMessages
    .filter(
      (existingMessage) =>
        !messages.find(
          (newMessage) => newMessage.messageId === existingMessage.messageId,
        ),
    )
    .concat(
      messages.filter(
        (message) => message.type === "STANDARD",
      ) as StandardMessage[],
    )
    .toSorted(sortMessages);

  const controlMessages = existingControlMessages
    .filter(
      (existingMessage) =>
        !messages.find(
          (newMessage) => newMessage.messageId === existingMessage.messageId,
        ),
    )
    .concat(
      messages.filter(
        (message) =>
          message.type === "CONTROL" && message.content !== "UNKNOWN",
      ) as ControlMessage[],
    )
    .toSorted(sortMessages);
  return { controlMessages, standardMessages };
}

export const messageAttributeMapper: MessageAttributeMapper = {
  toChimeMessageAttributes: (attributes) => {
    return attributes.attachments
      ? {
          Attachments: {
            StringValues: attributes.attachments.map((attachment) =>
              JSON.stringify(attachment),
            ),
          },
        }
      : undefined;
  },
  parseChimeMessageAttributes: (attributes) => {
    const attachments = attributes?.["Attachments"];

    let parsedAttachments = [];

    for (let rawAttachment of attachments?.StringValues ?? []) {
      const parsedAttachment = JSON.parse(rawAttachment);
      if (!parsedAttachment.fileObjectKey || !parsedAttachment.fileName) {
        continue;
      }
      parsedAttachments.push({
        fileObjectKey: parsedAttachment.fileObjectKey,
        fileName: parsedAttachment.fileName,
      });
    }
    const sentTimeOverrideString =
      attributes?.["SentTimeOverride"]?.StringValues?.[0];

    let sentTimeOverride = undefined;
    try {
      sentTimeOverride = sentTimeOverrideString
        ? parseISO(sentTimeOverrideString)
        : undefined;
    } catch (e) {
      console.warn("Unable to parse sentTimeOverride", e);
    }

    const journalEntryId = attributes?.["JournalEntryId"]?.StringValues?.[0];
    const patientLabId = attributes?.["PatientLabId"]?.StringValues?.[0];

    return {
      attachments: parsedAttachments,
      sentTimeOverride,
      journalEntryId,
      patientLabId,
      format:
        attributes?.["Format"]?.StringValues?.[0] === "html" ? "html" : "plain",
    };
  },
};

/**
 * Get the UUIDs from the ends of the ARNs, remove the hyphens, and concat
 * together to form Channel ARN.
 * @param providerArn
 * @param respondentArn
 */
const generateChannelArn = (providerArn: string, respondentArn: string) => {
  return `${
    process.env.REACT_APP_CHIME_SDK_APP_INSTANCE_ARN
  }/channel/${providerArn.replace(/-/g, "").split("/").pop()}${respondentArn
    .replace(/-/g, "")
    .split("/")
    .pop()}`;
};

/**
 * Extract two UUIDs from the end of the channel ARN, where they have had their
 * hyphens removed and been concatenated together, and return them in an object,
 * provider-first and patient-second, as ARNs of AppInstanceUsers.
 */
const extractMemberArnsFromChannelArn = (channelArn: string) => {
  // First grab the string at the end of the ARN.
  const combinedUuids = channelArn.split("/").pop()!;
  // Then split it into two hyphenless UUIDs, and apply the hyphens back.
  const [providerUuid, patientUuid] = [
    combinedUuids.slice(0, 32),
    combinedUuids.slice(32),
  ].map(
    (uuid) =>
      `${uuid.slice(0, 8)}-${uuid.slice(8, 12)}-${uuid.slice(
        12,
        16,
      )}-${uuid.slice(16, 20)}-${uuid.slice(20)}`,
  );

  return {
    providerArn: `${process.env.REACT_APP_CHIME_SDK_APP_INSTANCE_ARN}/user/${providerUuid}`,
    patientArn: `${process.env.REACT_APP_CHIME_SDK_APP_INSTANCE_ARN}/user/${patientUuid}`,
  };
};

type ConversationStore = {
  appInstanceUserArn: string;
  cognitoId: string;
  conversations: Record<string, Conversation>;
  chatUsers: Record<string, ChatUser>;
  hasReceivedColdBootMessages: boolean;
};

const convertSocketChannelMessageToAwsChannelMessageSummary = (
  message: SocketChannelMessage,
): ChannelMessageSummary => {
  return {
    ...message,
    CreatedTimestamp: parseISO(message.CreatedTimestamp),
    LastUpdatedTimestamp: parseISO(message.LastUpdatedTimestamp),
  };
};

const convertAwsChannelMessageSummaryToChannelMessage = (
  message: ChannelMessageSummary,
): ChannelMessageWithoutSender => {
  const attributes = messageAttributeMapper.parseChimeMessageAttributes(
    message.MessageAttributes!,
  );
  const createdTimestamp =
    attributes.sentTimeOverride !== undefined
      ? attributes.sentTimeOverride
      : message.CreatedTimestamp!;

  const baseMessage = {
    lastUpdatedTimestamp: message.LastUpdatedTimestamp!,
    createdTimestamp,
    messageId: message.MessageId!,
    senderArn: message.Sender!.Arn!,
  };

  if (message.Type === "STANDARD") {
    return {
      ...baseMessage,
      type: "STANDARD",
      content: message.Content!,
      attributes: attributes,
    };
  } else if (message.Type === "CONTROL") {
    const content = message.Content === "READ" ? "READ" : "UNKNOWN";
    return {
      ...baseMessage,
      type: "CONTROL",
      content,
    };
  }

  console.warn("Unknown message type", message);

  return {
    ...baseMessage,
    type: "CONTROL",
    content: "UNKNOWN",
  };
};

type ConversationStoreUpdateAction =
  | {
      type: "INGEST_MESSAGES";
      payload: {
        messages: ChannelMessageWithoutSender[];
        conversationId: string;
        hasMoreMessages?: boolean;
        nextConversationMessagesToken?: {
          nextToken: string;
          notAfter: Date | null;
        };
        didComeFromSocket?: boolean;
      };
    }
  | {
      type: "CHANNEL_DETAILS";
      payload: SocketChannelDetails;
    }
  | {
      type: "INGEST_CHAT_USERS";
      payload: {
        chatUsers: ChatUser[];
      };
    }
  | {
      type: "DELETE_MESSAGE";
      payload: {
        messageId: string;
        conversationId: string;
      };
    }
  | {
      type: "ENQUEUE_LOAD_CHANNEL";
      payload: {
        conversationId: string;
      };
    };

const bindMessageSenderWithChatUsers = (
  chatUsers: Record<string, ChatUser>,
  messageWithoutSender: ChannelMessageWithoutSender,
): ChannelMessage | null => {
  const sender = chatUsers[messageWithoutSender.senderArn];
  if (!sender) {
    // We used to throw an error, now we're going to warn. This should really only occur
    // in dev environments where there's a chance that app instance channels exist for patient/provider relationships
    // that have been fully _DELETED_ or are otherwise inaccessible by the provider instance,
    // e.g. if the provider was deleted from the db to reimport.
    logger.warn(
      `Sender ChatUser missing for senderArn ${messageWithoutSender.senderArn} -- skipping`,
    );

    return null;
  }

  return {
    ...messageWithoutSender,
    sender,
  };
};

const chatReducer = (
  state: ConversationStore,
  action: ConversationStoreUpdateAction,
): ConversationStore => {
  const bindMessageSender = (message: ChannelMessageWithoutSender) =>
    bindMessageSenderWithChatUsers(state.chatUsers, message);

  if (action.type === "INGEST_CHAT_USERS") {
    const { chatUsers } = action.payload;
    const newChatUsers = chatUsers.reduce(
      (acc, chatUser) => {
        acc[chatUser.chimeArn] = chatUser;
        return acc;
      },
      { ...state.chatUsers },
    );

    return {
      ...state,
      conversations: Object.values(newChatUsers)
        .filter((chatUser) => chatUser.chimeArn !== state.appInstanceUserArn)
        .reduce(
          (acc, chatUser) => {
            const conversationId = generateChannelArn(
              chatUser.isProvider
                ? chatUser.chimeArn
                : state.appInstanceUserArn,
              chatUser.isProvider
                ? state.appInstanceUserArn
                : chatUser.chimeArn,
            );

            const existingConversation =
              state.conversations[conversationId] ?? {};

            acc[conversationId] = {
              // Default
              ...{
                id: conversationId,
                name: "Fay Conversation",
                hasUnreadMessage: false,
                nextConversationMessagesToken: undefined,
                lastMessageDate: null,
                messages: [],
                controlMessages: [],
                hasMoreMessages: true,
                sortKey: "createdAt",
                hasTriedToLoadOrReceivedMessages: false,
                shouldAttemptLoadingChannel: false,
              },
              // Default from existing state (including messages, control messages, last message date, etc.)
              // anything that gets populated in other reducers
              ...existingConversation,
              // New state that should get absolute priority
              ...{
                selfChimeArn: state.appInstanceUserArn,
                self: state.chatUsers[state.appInstanceUserArn],
                respondentChimeArn: chatUser.chimeArn,
                respondent: chatUser,
              },
            };

            return acc;
          },
          { ...state.conversations },
        ),
      chatUsers: newChatUsers,
    };
  } else if (action.type === "INGEST_MESSAGES") {
    const {
      messages: messagesWithoutSender,
      conversationId,
      hasMoreMessages,
      nextConversationMessagesToken,
      didComeFromSocket,
    } = action.payload;

    let conversation = state.conversations[conversationId];

    if (!conversation) {
      const { providerArn, patientArn } =
        extractMemberArnsFromChannelArn(conversationId);

      const respondentArn =
        state.appInstanceUserArn === providerArn ? patientArn : providerArn;

      if (!state.chatUsers[respondentArn]) {
        console.warn(
          `Missing ChatUser for Respondent Chime ARN: ${respondentArn}`,
        );

        return state;
      }

      conversation = {
        id: conversationId,
        name: "Fay Conversation",
        selfChimeArn: state.appInstanceUserArn,
        self: state.chatUsers[state.appInstanceUserArn],
        respondentChimeArn: respondentArn,
        respondent: state.chatUsers[respondentArn],
        hasUnreadMessage: false,
        nextConversationMessagesToken: undefined,
        lastMessageDate: null,
        messages: [],
        controlMessages: [],
        hasMoreMessages: true,
        hasTriedToLoadOrReceivedMessages: false,
        shouldAttemptLoadingChannel: false,
        sortKey: "lastMessageDate",
      };
    }

    const messages = messagesWithoutSender
      .map(bindMessageSender)
      .filter((msg) => msg !== null) as ChannelMessage[];

    const { controlMessages, standardMessages } =
      mergeControlAndStandardMessages(
        conversation.messages,
        conversation.controlMessages,
        messages,
      );

    const hasUnreadMessage = checkHasUnreadMessages(
      conversation.selfChimeArn,
      controlMessages,
      standardMessages,
    );

    const newLastMessageDate =
      standardMessages.map((message) => message.createdTimestamp).at(-1) ??
      conversation.lastMessageDate;

    return {
      ...state,
      hasReceivedColdBootMessages: true,
      conversations: {
        ...state.conversations,
        [conversationId]: {
          ...conversation,
          hasMoreMessages: hasMoreMessages ?? conversation.hasMoreMessages,
          hasUnreadMessage,
          lastMessageDate: newLastMessageDate,
          messages: standardMessages,
          controlMessages: controlMessages,
          nextConversationMessagesToken,
          hasTriedToLoadOrReceivedMessages: true,
          sortKey: didComeFromSocket ? "lastMessageDate" : conversation.sortKey,
        },
      },
    };
  } else if (action.type === "DELETE_MESSAGE") {
    const { conversationId, messageId } = action.payload;

    const conversation = state.conversations[conversationId];

    if (!conversation) {
      return state;
    }

    return {
      ...state,
      conversations: {
        ...state.conversations,
        [conversationId]: {
          ...conversation,
          messages: conversation.messages.filter(
            (message) => message.messageId !== messageId,
          ),
        },
      },
    };
  } else if (action.type === "CHANNEL_DETAILS") {
    const channelDetails = action.payload;
    const { Channel, ChannelMemberships, ChannelMessagesHasMore } =
      channelDetails;

    const selfMembership = ChannelMemberships.find(
      (membership: ChannelMembershipSummary) =>
        membership.Member?.Arn === state.appInstanceUserArn,
    );
    const respondentMembership = ChannelMemberships.find(
      (membership: ChannelMembershipSummary) =>
        membership.Member?.Arn !== state.appInstanceUserArn,
    );

    const messages = channelDetails.ChannelMessages.map(
      convertSocketChannelMessageToAwsChannelMessageSummary,
    )
      .map(convertAwsChannelMessageSummaryToChannelMessage)
      .map(bindMessageSender)
      .filter((msg) => msg !== null) as ChannelMessage[];

    const { controlMessages, standardMessages } =
      mergeControlAndStandardMessages([], [], messages);

    const hasUnreadMessage = checkHasUnreadMessages(
      state.appInstanceUserArn,
      controlMessages,
      standardMessages,
    );

    const selfChimeArn = selfMembership?.Member?.Arn!;
    const self = state.chatUsers[selfChimeArn];

    const respondentChimeArn = respondentMembership?.Member?.Arn!;
    const respondent = state.chatUsers[respondentChimeArn];

    if (!self) {
      console.error(`Missing ChatUser for Self Chime ARN: ${selfChimeArn}`);
      toast("We've encountered an error with chat. Please try again later.");
      throw new Error(`Missing ChatUser for Self: ${selfChimeArn}`);
    }

    if (!respondent) {
      console.error(
        `Missing ChatUser for Respondent Chime ARN: ${respondentChimeArn}`,
      );
      return state;
    }

    const existingConversation = state.conversations[Channel.ChannelArn!] ?? {};

    const conversation: Conversation = {
      ...existingConversation,
      id: Channel.ChannelArn!,
      name: Channel.Name!,
      hasMoreMessages: ChannelMessagesHasMore,
      hasUnreadMessage,
      nextConversationMessagesToken: undefined,
      lastMessageDate:
        standardMessages.map((message) => message.createdTimestamp).at(-1) ??
        null,
      selfChimeArn,
      self,
      respondentChimeArn,
      respondent,
      messages: standardMessages,
      controlMessages,
      sortKey: "lastMessageDate",
    };

    return {
      ...state,
      conversations: {
        ...state.conversations,
        [conversation.id]: {
          ...conversation,
          hasUnreadMessage,
          messages: standardMessages,
          controlMessages: controlMessages,
          hasTriedToLoadOrReceivedMessages: true,
          shouldAttemptLoadingChannel: false,
        },
      },
      hasReceivedColdBootMessages: true,
    };
  } else if (action.type === "ENQUEUE_LOAD_CHANNEL") {
    const { conversationId } = action.payload;

    const conversation = state.conversations[conversationId];

    if (!conversation) {
      return state;
    }

    return {
      ...state,
      conversations: {
        ...state.conversations,
        [conversationId]: {
          ...conversation,
          shouldAttemptLoadingChannel: true,
        },
      },
    };
  }
  return state;
};

export const useChat = (): ChatState => {
  const value = useContext(ChatContext);

  if (!value) {
    throw new Error("useChat must be used within ChatProvider");
  }

  return value;
};

const sortMessages = (a: BaseMessage, b: BaseMessage) => {
  // Sort by CreatedTimestamp, ascending.
  return a.createdTimestamp.getTime() - b.createdTimestamp.getTime();
};

interface ChatProviderProps {
  children: ReactNode;
  chatUsers: ChatUser[];
  areChatUsersReady: boolean;
  respondentEntityName: string;
  chatBoxPlaceholderText: string;
  shouldAutomaticallyMarkSelectedConversationAsRead?: boolean;
  respondentDisplayNameModifier?: string;
  shouldSuggestDrafts: boolean;
}

const ChatProvider = ({
  children,
  chatUsers,
  areChatUsersReady,
  respondentEntityName,
  chatBoxPlaceholderText,
  shouldAutomaticallyMarkSelectedConversationAsRead,
  respondentDisplayNameModifier,
  shouldSuggestDrafts,
}: ChatProviderProps) => {
  const { respondentId } = useParams();

  // We switched from internally maintaining the state of the selected conversation
  // to reading the URL for the selected conversation. This makes it difficult to
  // send a message from outside the Chat route using `useChat`.
  //
  // To send a message to a conversation from outside the chat route,
  // the workaround I chose was to use an `initialMessage` parameter to send a
  // message to the conversation by navigating to it.
  //
  // This may be something we choose to rewrite if we have to send messages here
  // without navigating.
  const [searchParams, setSearchParams] = useSearchParams();
  const initialMessage = searchParams.get("initialMessage") ?? undefined;
  const clearInitialMessage = useCallback(() => {
    setSearchParams((prev) => {
      const newParams = new URLSearchParams(prev);
      newParams.delete("initialMessage");
      return newParams;
    });
  }, [setSearchParams]);

  const { cognitoId } = useAuth();

  const appInstanceUserArn = `${process.env.REACT_APP_CHIME_SDK_APP_INSTANCE_ARN}/user/${cognitoId}`;

  // TODO: New channels may appear while the user is logged in.
  // Do we care about keeping this list updated?
  const [conversationStore, dispatchUpdateConversationStore] = useReducer(
    chatReducer,
    {
      appInstanceUserArn,
      cognitoId,
      conversations: {},
      chatUsers: {},
      hasReceivedColdBootMessages: false,
    },
  );
  const conversations = useMemo(() => {
    return Object.values(conversationStore.conversations).toSorted((a, b) => {
      if (conversationStore.hasReceivedColdBootMessages) {
        return (
          (b.lastMessageDate?.getTime() ?? 0) -
          (a.lastMessageDate?.getTime() ?? 0)
        );
      } else {
        return (
          (b.respondent.createdAt?.getTime() ?? 0) -
          (a.respondent.createdAt?.getTime() ?? 0)
        );
      }
    });
  }, [
    conversationStore.conversations,
    conversationStore.hasReceivedColdBootMessages,
  ]);

  const [messagingClient, setMessagingClient] =
    useState<ChimeSDKMessagingClient>();

  const [session, setSession] = useState<MessagingSession>();
  const isConnecting = !session;

  const [clientSearchQuery, setClientSearchQuery] = useState<string>("");

  const selectedConversation = useMemo(() => {
    return (
      conversations.find(
        (conversation) =>
          conversation.respondent.patientOrProviderId === respondentId,
      ) ?? null
    );
  }, [respondentId, conversations]);

  useEffect(() => {
    dispatchUpdateConversationStore({
      type: "INGEST_CHAT_USERS",
      payload: {
        chatUsers,
      },
    });
  }, [chatUsers]);

  /**
   * Set up the observer for new events and messages from the session.
   * This includes control and standard messages
   */
  const observer: MessagingSessionObserver = useMemo(
    () => ({
      messagingSessionDidStartConnecting(reconnecting) {
        logger.info("Connecting to chat", { reconnecting });
      },
      messagingSessionDidStop(event) {
        logger.info("Stopped chat", { event });
      },
      messagingSessionDidStart() {
        logger.info("Started chat");
      },
      messagingSessionDidReceiveMessage: (message): void => {
        if (message.type === "CREATE_CHANNEL_MESSAGE") {
          const messagePayload: SocketChannelMessage = JSON.parse(
            message.payload,
          );
          const { ChannelArn } = messagePayload;

          dispatchUpdateConversationStore({
            type: "INGEST_MESSAGES",
            payload: {
              messages: [
                convertAwsChannelMessageSummaryToChannelMessage(
                  convertSocketChannelMessageToAwsChannelMessageSummary(
                    messagePayload,
                  ),
                ),
              ],
              conversationId: ChannelArn,
              didComeFromSocket: true,
            },
          });
        } else if (message.type === "DELETE_CHANNEL_MESSAGE") {
          const messagePayload = JSON.parse(message.payload);
          const { ChannelArn, MessageId } = messagePayload;

          dispatchUpdateConversationStore({
            type: "DELETE_MESSAGE",
            payload: {
              messageId: MessageId,
              conversationId: ChannelArn,
            },
          });
        } else if (message.type === "CHANNEL_DETAILS") {
          const channelDetails: SocketChannelDetails = JSON.parse(
            message.payload,
          );

          dispatchUpdateConversationStore({
            type: "CHANNEL_DETAILS",
            payload: channelDetails,
          });
        } else {
          logger.debug(message, JSON.parse(message.payload));
        }
      },
    }),
    [],
  );

  /**
   * Configure the Chime client and session
   */
  useEffect(() => {
    if (cognitoId && areChatUsersReady) {
      (async () => {
        const response = await chatService({
          appInstanceUserArn,
        });

        try {
          response.session.addObserver(observer);
          await response.session.start();
        } catch (e) {
          Sentry.captureException(e, {
            tags: { manuallyInstrumented: "true" },
          });
          logger.error("Failed to start session", e);
          toast.error(
            "Could not connect to chat; Our team has been notified, please try again later!",
          );
        }

        // Set the messaging client and session even if error occured during start in case of successful retry
        setMessagingClient(response.messagingClient);
        setSession(response.session);
      })();
    }

    return () => {
      if (session) {
        session.stop();
      }

      if (messagingClient) {
        messagingClient.destroy();
      }
    };
  }, [cognitoId, appInstanceUserArn, areChatUsersReady, observer]); // eslint-disable-line react-hooks/exhaustive-deps

  const unreadConversationCount = useMemo(() => {
    return conversations.filter((conversation) => conversation.hasUnreadMessage)
      .length;
  }, [conversations]);

  // Use a control message to send a read receipt
  const sendChannelReadReceipt = useCallback(
    async (conversation?: Conversation) => {
      if (messagingClient && (selectedConversation || conversation)) {
        // Check if a chat is unread before sending a read receipt.
        if (
          (conversation && !conversation?.hasUnreadMessage) ||
          (!conversation && !selectedConversation?.hasUnreadMessage)
        ) {
          return;
        }

        await messagingClient.send(
          new SendChannelMessageCommand({
            ChannelArn: conversation?.id ?? selectedConversation?.id,
            ChimeBearer: appInstanceUserArn,
            Content: "READ",
            Type: "CONTROL",
            Persistence: "PERSISTENT",
          }),
        );
      }
    },
    [messagingClient, selectedConversation, appInstanceUserArn],
  );

  const sendChannelMessageFn = useCallback(
    async ({
      message,
      attributes,
      recipientId,
    }: {
      message: string;
      attributes?: FayMessageAttributes;
      recipientId?: string;
    }) => {
      // Only remove whitespace at the beginning and end of the message
      // Previously we consolidated internal whitespace but have changed our minds about that.
      const cleanMessage = message.trim();

      // Chime always requires text to be sent
      // we send a space if there is no message but are attachments as a workaround
      const contentToSend = attributes?.attachments?.length
        ? cleanMessage || " "
        : cleanMessage;

      if (!contentToSend.length) {
        return;
      }

      const conversationToMessage = recipientId
        ? conversations.find(
            (conversation) =>
              conversation.respondent.patientOrProviderId === recipientId,
          ) ?? null
        : selectedConversation;

      // TODO: this is an asynchronous call. Do we want to append a fake message
      // in case the call is slow?
      if (messagingClient && conversationToMessage) {
        try {
          await messagingClient.send(
            new SendChannelMessageCommand({
              ChannelArn: conversationToMessage.id,
              ChimeBearer: appInstanceUserArn,
              Content: contentToSend,
              MessageAttributes: attributes
                ? messageAttributeMapper.toChimeMessageAttributes(attributes)
                : {},
              Type: "STANDARD",
              Persistence: "PERSISTENT",
            }),
          );
        } catch (e: any) {
          toast.error(
            "Failed to send message; Our team has been notified, please try again later!",
          );

          // Transient network errors are fixed with a user retry
          if (
            e instanceof TypeError &&
            (e.message === "Failed to fetch" || e.message === "Load failed")
          ) {
            return;
          }

          Sentry.captureException(e, {
            tags: { manuallyInstrumented: "true" },
          });
          throw e;
        }
      }
    },
    [messagingClient, selectedConversation, appInstanceUserArn, conversations],
  );

  const {
    mutateAsync: sendChannelMessage,
    isPending: isSendingChannelMessage,
  } = useMutation({
    mutationFn: sendChannelMessageFn,
  });

  useEffect(() => {
    if (
      messagingClient &&
      initialMessage &&
      selectedConversation &&
      !isSendingChannelMessage
    ) {
      clearInitialMessage();
      sendChannelMessage({ message: initialMessage });
    }
  }, [
    messagingClient,
    initialMessage,
    selectedConversation,
    sendChannelMessage,
    clearInitialMessage,
    isSendingChannelMessage,
  ]);

  const { enqueue } = useLoadingQueue({
    processItem: (item: { id: string }) => {
      return loadMoreChatHistoryForConversation(item.id);
    },
  });

  const enqueueLoadConversation = useCallback(
    (conversationId: string) => {
      const conversation = conversationStore.conversations[conversationId];

      if (!conversation) {
        return;
      }

      if (conversation.shouldAttemptLoadingChannel) {
        return;
      }

      if (conversation.hasTriedToLoadOrReceivedMessages) {
        return;
      }

      dispatchUpdateConversationStore({
        type: "ENQUEUE_LOAD_CHANNEL",
        payload: {
          conversationId,
        },
      });

      enqueue({ id: conversationId });
    },
    [conversationStore.conversations, enqueue],
  );

  const loadMoreChatHistoryForConversation = useCallback(
    async (conversationId: string) => {
      const conversation = conversationStore.conversations[conversationId];

      const shouldLoad =
        conversation &&
        (conversation.hasMoreMessages ||
          !conversation.hasTriedToLoadOrReceivedMessages);

      let { nextToken, notAfter } =
        conversation?.nextConversationMessagesToken ?? {};

      if (messagingClient) {
        if (!shouldLoad) {
          return;
        }

        let allMessages: ChannelMessageWithoutSender[] = [];

        if (notAfter === undefined) {
          notAfter = conversation?.lastMessageDate ?? null;
        }

        while (true) {
          // Fetch until there are none left, or we find a standard message.
          // This is important if there are 50 control messages in a row
          // (which I encountered due to a buggy READ receipt implementation)
          const listMessagesCommand = new ListChannelMessagesCommand({
            ChannelArn: conversationId,
            MaxResults: 50,
            // NOTE: This keeps the results new!
            // NOTE: "... === null" This is a bit of a hack to handle the case where the last message date is null
            // which can happen if we're persisting a nextToken that doesn't have a notAfter
            // because the conversation is initially empty.
            NotAfter: notAfter === null ? undefined : notAfter,
            SortOrder: "DESCENDING",
            ChimeBearer: appInstanceUserArn,
            NextToken: nextToken,
          });

          let response;

          try {
            response = await messagingClient.send(listMessagesCommand);
          } catch (e) {
            console.error("Failed to load more chat history", conversationId);
            return;
          }

          const messages =
            response.ChannelMessages?.map(
              convertAwsChannelMessageSummaryToChannelMessage,
            ) ?? [];

          allMessages = allMessages.concat(messages);
          nextToken = response.NextToken;

          if (
            messages.some((message) => message.type === "STANDARD") ||
            nextToken === undefined
          ) {
            break;
          }
        }

        dispatchUpdateConversationStore({
          type: "INGEST_MESSAGES",
          payload: {
            messages: allMessages,
            conversationId: conversationId,
            hasMoreMessages: nextToken !== undefined,
            nextConversationMessagesToken: nextToken
              ? {
                  nextToken: nextToken,
                  notAfter: notAfter,
                }
              : undefined,
            didComeFromSocket: false,
          },
        });
      }
    },
    [messagingClient, conversationStore, appInstanceUserArn],
  );

  const loadMoreChatHistoryForSelectedConversation = useCallback(async () => {
    if (selectedConversation) {
      await loadMoreChatHistoryForConversation(selectedConversation.id);
    }
  }, [loadMoreChatHistoryForConversation, selectedConversation]);

  useEffect(() => {
    if (
      messagingClient &&
      selectedConversation &&
      selectedConversation.messages.length === 0
    ) {
      (async () => {
        await loadMoreChatHistoryForConversation(selectedConversation.id);
      })();
    }
  }, [
    messagingClient,
    selectedConversation,
    loadMoreChatHistoryForConversation,
  ]);

  return (
    <ChatContext.Provider
      value={{
        isConnecting,
        session,
        messagingClient,
        conversations,
        clientSearchQuery,
        setClientSearchQuery,
        selectedConversation,
        sendChannelReadReceipt,
        unreadConversationCount,
        sendChannelMessage,
        isSendingChannelMessage,
        loadMoreChatHistoryForConversation,
        loadMoreChatHistoryForSelectedConversation,
        shouldAutomaticallyMarkSelectedConversationAsRead,
        respondentEntityName,
        chatBoxPlaceholderText,
        respondentDisplayNameModifier,
        shouldSuggestDrafts,
        enqueueLoadConversation,
      }}
    >
      {children}
    </ChatContext.Provider>
  );
};

export const ProviderChatProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const { patients, hasFetchedPatients } = usePatients({
    withOldPatients: true,
  });
  const { providerDetails } = useProviderDetails("me");
  const { cognitoId } = useAuth();
  const respondentEntityName = "clients";

  const chatBoxPlaceholderText = "Message ${respondentName}"; // eslint-disable-line no-template-curly-in-string

  const chatUsers = useMemo(
    () => [
      {
        chimeArn: `${process.env.REACT_APP_CHIME_SDK_APP_INSTANCE_ARN}/user/${
          cognitoId ?? ""
        }`,
        cognitoId: cognitoId ?? "",
        isProvider: true,
        patientOrProviderId: providerDetails?.provider_id ?? "",
        firstName: providerDetails?.preferred_first_name ?? "",
        lastName: providerDetails?.last_name ?? "",
        displayName: providerDetails?.full_name ?? "",
        avatarUrl: providerDetails?.avatar_url ?? "",
        hasActiveRelationship: true,
        createdAt: new Date(), // For self it doesn't really matter.
      },
      ...patients.map((patient) => ({
        chimeArn: `${process.env.REACT_APP_CHIME_SDK_APP_INSTANCE_ARN}/user/${patient.cognito_id}`,
        cognitoId: patient.cognito_id,
        isProvider: false,
        patientOrProviderId: patient.patient_id,
        firstName: patient.preferred_first_name,
        lastName: patient.last_name,
        displayName: patient.full_name,
        avatarUrl: patient.avatar_url ?? "",
        hasActiveRelationship:
          providerDetails?.provider_id === patient.current_provider_id,
        createdAt: patient.joined_at ?? null,
        profileLink:
          providerDetails?.provider_id === patient.current_provider_id
            ? `/provider/clients/${patient.patient_id}`
            : undefined,
      })),
    ],
    [cognitoId, providerDetails, patients],
  );

  return (
    <ChatProvider
      chatUsers={chatUsers}
      areChatUsersReady={hasFetchedPatients}
      respondentEntityName={respondentEntityName}
      chatBoxPlaceholderText={chatBoxPlaceholderText}
      shouldSuggestDrafts={true}
    >
      {children}
    </ChatProvider>
  );
};

export const ClientChatProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const { providers, hasFetchedProviders } = usePatientProviders();
  const { patient } = usePatientDetails("me");

  const respondentEntityName = "dietitians";
  const respondentDisplayNameModifier = ", RD";

  const chatBoxPlaceholderText = "Message ${respondentName}"; // eslint-disable-line no-template-curly-in-string

  const chatUsers = useMemo(
    () => [
      {
        chimeArn: `${process.env.REACT_APP_CHIME_SDK_APP_INSTANCE_ARN}/user/${
          patient?.cognito_id ?? ""
        }`,
        cognitoId: patient?.cognito_id ?? "",
        isProvider: false,
        patientOrProviderId: patient?.patient_id ?? "",
        firstName: patient?.preferred_first_name ?? "",
        lastName: patient?.last_name ?? "",
        displayName: patient?.full_name ?? "",
        avatarUrl: patient?.avatar_url ?? "",
        hasActiveRelationship: true,
        createdAt: new Date(), // For self it doesn't really matter.
      },
      ...providers.map((provider) => {
        const hasActiveRelationship =
          provider.provider_id === patient?.current_provider_id &&
          !provider.is_churned;

        return {
          chimeArn: `${process.env.REACT_APP_CHIME_SDK_APP_INSTANCE_ARN}/user/${provider.cognito_id}`,
          cognitoId: provider.cognito_id,
          isProvider: true,
          patientOrProviderId: provider.provider_id,
          firstName: provider.preferred_first_name,
          lastName: provider.last_name,
          displayName: provider.full_name,
          avatarUrl: provider.avatar_url ?? "",
          hasActiveRelationship,
          createdAt: hasActiveRelationship ? new Date() : null,
          profileLink: `https://faynutrition.com/dietitians/${provider.slug}`,
        };
      }),
    ],
    [patient, providers],
  );

  return (
    <ChatProvider
      chatUsers={chatUsers}
      areChatUsersReady={hasFetchedProviders}
      respondentEntityName={respondentEntityName}
      respondentDisplayNameModifier={respondentDisplayNameModifier}
      shouldAutomaticallyMarkSelectedConversationAsRead
      chatBoxPlaceholderText={chatBoxPlaceholderText}
      shouldSuggestDrafts={false}
    >
      {children}
    </ChatProvider>
  );
};
